import Loading from "components/Common/Loading";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import userRoleService from "services/UserRoleService";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import { IconButton, Tooltip } from "@mui/material";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import CreateEditUserRoleModal from "./CreateEditUserRoleModal";
import DeleteUserRoleModal from "./DeleteUserRoleModal";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const UserRoles = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		name: { min: 200 },              // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		actions: { min: 100, max: 100 }, // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	const [isLoading, setIsLoading] = useState(false);
	const [userRoles, setUserRoles] = useState([]);
	const [userRolesOpenModal, setUserRolesOpenModal] = useState(false);
	const [dataUserRolesModal, setDataUserRolesModal] = useState(null);
	const [deleteUserRoleOpenModal, setDeleteUserRoleOpenModal] =
		useState(null);

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	document.title = "Senda | Funções";

	const getItems = async () => {
		try {
			setIsLoading(true);
			const response = await userRoleService.get();
			setUserRoles(response);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			console.log(error);
		}
	};

	useEffect(() => {
		getItems();
	}, []);

	const handleCreateEditUserRoles = (rowData) => {
		setDataUserRolesModal(rowData);
		setUserRolesOpenModal(true);
	};

	const handleCloseCreateEditUserRoles = () => {
		setUserRolesOpenModal(false);
		getItems();
	};

	const handleDeleteUserRole = async (id) => {
		try {
			setIsLoading(true);
			await userRoleService.delete(id);
			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Função deletada com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			await SendaCustomSwal.fire({
				title: "Erro",
				text: error.message,
				iconHtml: '<i class="error-icon"></i>',
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		handleCloseDeleteUserRoleModal();
	};

	const handleDeleteUserRoleOpenModal = (rowData) => {
		setDataUserRolesModal(rowData);
		setDeleteUserRoleOpenModal(true);
	};

	const handleCloseDeleteUserRoleModal = () => {
		setDeleteUserRoleOpenModal(false);
		getItems();
	};

	const columns = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidth("name"),
				minWidthPixels: COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth("actions"),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ 
						display: "flex", 
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
					}}>
						<div style={{ marginRight: 10 }}>
							<Tooltip title="Editar" placement="top">
								<IconButton
									id={`editar-${row.original.id}`}
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() =>
										handleCreateEditUserRoles(row.original)
									}
								>
									<PencilSimple size={14} />
								</IconButton>
							</Tooltip>
						</div>
						<div style={{ marginRight: 10 }}>
							<Tooltip title="Excluir" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() =>
										handleDeleteUserRoleOpenModal(
											row.original,
										)
									}
								>
									<Trash size={14} />
								</IconButton>
							</Tooltip>
						</div>
					</div>
				),
			},
		],
		[zoomLevel], // Adicionado zoomLevel como dependência
	);

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				<Breadcrumbs
					onBotaoClick={() => handleCreateEditUserRoles({})}
					botao={"Nova função"}
					title="Funções"
				/>
				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<DynamicMaterialTable columns={columns} data={userRoles} />
				</div>
			</div>

			<CreateEditUserRoleModal
				show={userRolesOpenModal}
				data={dataUserRolesModal}
				onCloseClick={handleCloseCreateEditUserRoles}
			/>

			<DeleteUserRoleModal
				show={deleteUserRoleOpenModal}
				onCloseClick={handleCloseDeleteUserRoleModal}
				data={dataUserRolesModal}
				onDeleteClick={handleDeleteUserRole}
			/>
		</Loading>
	);
};

UserRoles.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(UserRoles);
